import { withIronSessionSsr } from 'iron-session/next'
import { GetServerSideProps, NextPage } from 'next'
import React, { useEffect } from 'react'
import { Image, Product, Variant } from 'shopify-buy'
import { AddToCart } from '../../components/AddToCart'
import { ImageContainer } from '../../components/ImageContainer'
import Carousel from '../../components/Carousel'
import { sessionOptions } from '../../lib/session'
import {
  filterSagaVariantByMarket,
  parseShopifyResponse,
} from '../../lib/shopify'
import { fetchProductDetailByHandle } from '../../lib/shopify/products'
import { IMAGE_FALLBACK, toTitleCase, USDollar } from '../../lib/utils'
import { SAGA_PHONE_PRODUCT_IDS } from '../../utils/constants'
import { useCheckoutCart } from '../../context/useCheckoutCart'
import HTMLHead from '../../components/HTMLHead'
import { fetchProductSeo } from '../../lib/shopify/productSeo'
import { ProductSeo } from '../../utils/productSeoDef'

const getSagaDetailImageSrc = (product: Product) => {
  return product?.images || product.variants?.[0] || IMAGE_FALLBACK
}

const getDetailImageSrc = (variant: Variant) => {
  return !variant?.image ? IMAGE_FALLBACK : variant.image.src
}

const getSagaIncludedImagesSrc = (isSaga: boolean, product: Product) => {
  let cableImage, seedSheet
  if (isSaga) {
    cableImage = product?.images?.[2]?.src || IMAGE_FALLBACK
    seedSheet = product?.images?.[3]?.src || IMAGE_FALLBACK
  }
  return { cableImage, seedSheet }
}

const FILTER_AFTER_INDEX = 5

const filterImages = (image: Image, index: number) =>
  index >= FILTER_AFTER_INDEX

const ProductDetail: NextPage<{
  variant: Variant
  product: Product
  verifiedWallet: string
  productSeo: ProductSeo | null
}> = ({ variant, verifiedWallet, product, productSeo }) => {
  const { setProductVariant, loadProduct } = useCheckoutCart()
  const isSaga = SAGA_PHONE_PRODUCT_IDS.includes(product.id as string)

  useEffect(() => {
    if (isSaga) {
      setProductVariant({
        product,
        productId: product.id as string,
        variant,
      })
    } else {
      loadProduct()
    }
  }, [isSaga, variant, product, loadProduct, setProductVariant])

  const parsedAmount = USDollar.format(parseFloat(`${variant.priceV2.amount}`))

  const detailImage = getDetailImageSrc(variant)
  const sagaDetailImages = getSagaDetailImageSrc(product)
  const { cableImage, seedSheet } = getSagaIncludedImagesSrc(isSaga, product)

  const htmlTitle = productSeo?.title
    ? productSeo.title
    : toTitleCase(product.title)
  const htmlDescription = productSeo?.description
    ? productSeo.description
    : product?.description
    ? product.description
    : ''

  const oldPrice =
    isSaga && parseInt(`${variant.priceV2.amount}`) < 1000
      ? USDollar.format(parseFloat(`1000.00`))
      : ''

  return (
    <>
      <HTMLHead title={htmlTitle} description={htmlDescription} />
      <div
        style={{ position: 'relative' }}
        className="flex flex-col pt-5 lg:pt-14 mb-10"
      >
        <section className="flex flex-col lg:items-start lg:flex-row lg:gap-14 h-full justify-around min-h-[580px]">
          <div className="relative min-h-[380px] lg:min-h-[400px] aspect-[1/1] sm:aspect-[4/3] w-full">
            {isSaga ? (
              <Carousel
                images={sagaDetailImages}
                priority={true}
                filterImages={filterImages}
              />
            ) : (
              <ImageContainer src={detailImage} priority={true} />
            )}
          </div>
          <div className="pt-5 md:pt-0 flex items-center justify-center w-full sm:max-w-[750px]">
            <div
              className={
                'flex flex-col lg:flex-col sm:flex-row grow lg:grow-0 gap-2 lg:gap-0 lg:max-w-full sm:justify-around'
              }
            >
              <div className={'flex flex-col gap-r-4 md:gap-0'}>
                <span className=" text-4xl mb-5 font-bold">
                  {' '}
                  {product.title}
                </span>
                <span className=" text-lg mb-10 font-medium">
                  {oldPrice && <span className="line-through">{oldPrice}</span>}{' '}
                  {parsedAmount}
                </span>
                <div
                  className="list-style text-[14px] mb-10 sm:pr-4 lg:pr-0 sm:max-w-[325px]"
                  dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                ></div>
              </div>
              <div>
                <AddToCart
                  verifiedWallet={verifiedWallet}
                  variant={variant}
                  showFaq={isSaga}
                  showInfo
                />
              </div>
            </div>
          </div>
        </section>
        {isSaga && (
          <section className="gap-14 relative mt-10">
            <span className="text-4xl font-semibold">What&apos;s included</span>
            <div className="grid lg:grid-cols-2 gap-10 mt-5">
              <div className="col-span-1 ">
                <div className="relative min-h-[340x] aspect-square lg:aspect-video w-full mb-5">
                  <ImageContainer src={cableImage || variant.image.src} />
                </div>
                <span className="text-[17px] font-medium">
                  USB-C charging cable, with data privacy
                </span>
              </div>
              <div className="col-span-1 ">
                <div className="relative min-h-[340x] aspect-square lg:aspect-video mb-5">
                  <ImageContainer src={seedSheet || variant.image.src} />
                </div>
                <span> Seed Phrase recovery sheets</span>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = withIronSessionSsr(
  async (context) => {
    const { locale } = context
    const { productId } = context.query
    const { verifiedWallet } = context.req.session

    const product =
      typeof productId === 'string' &&
      (await fetchProductDetailByHandle(productId))

    if (!product) {
      return {
        notFound: true,
      }
    }
    let variant: Variant = product.variants[0]

    const productSeo = await fetchProductSeo(product)

    if (SAGA_PHONE_PRODUCT_IDS.includes(product.id.toString())) {
      try {
        variant = filterSagaVariantByMarket([product], locale || 'US').variant
      } catch (error) {
        return {
          notFound: true,
        }
      }
    }
    variant.product = { ...product, variants: [] }

    return {
      props: {
        variant: parseShopifyResponse(variant),
        product: parseShopifyResponse(product),
        productSeo,
        verifiedWallet: verifiedWallet ?? null,
      },
    }
  },
  sessionOptions
)

export default ProductDetail
